import React, { useMemo } from 'react';
import { DarwinCol, DarwinRow } from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { SettingOutlined } from '@ant-design/icons';
import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../../Users.module.css';
import { NewUserAssociatedProductsAndProcessesInput } from '../inputs/NewUserAssociatedProductsAndProcessesInput';
import { useGetProductsTree } from 'api/services/productsService';
import { useUI } from 'contexts/UiContext';
import { extractQueryData } from 'utils/services';
import { useGetFdaProcesses } from 'api/services/processesService';
import { extractProcessNames, extractProductNames } from '../utils';
import { CreateUserFormData, CreateUserFormInputProps } from 'types/userTypes';
import { InputType } from 'types/genericTypes';
import { GenericInput } from 'components/ui/GenericInput';

const { Title } = DarwinTypography;

interface MetadataSectionProps {
  control: Control<CreateUserFormData>;
  errors: FieldErrors<CreateUserFormData>;
  isEditForm: boolean;
}

export const MetadataSection: React.FC<MetadataSectionProps> = ({
  control,
  errors,
  isEditForm,
}) => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const productTreeData = useGetProductsTree(selectedCompany || '');
  const productsData = extractQueryData(productTreeData, []);
  const fdaProcesses = useGetFdaProcesses();
  const fdaProcessesData = extractQueryData(fdaProcesses, []);

  const productsNames = useMemo(
    () => extractProductNames(productsData),
    [productsData]
  );

  const processesNames = useMemo(
    () => extractProcessNames(fdaProcessesData),
    [fdaProcessesData]
  );

  const firstColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'createdBy',
      label: t('view-user-form.created-by-input'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: true,
    },
    {
      inputType: InputType.DATEPICKER,
      fieldName: 'createdAt',
      label: t('view-user-form.created-date-input'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: true,
    },
  ];

  const secondColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'updatedBy',
      label: t('view-user-form.modified-by-input'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: true,
    },
    {
      inputType: InputType.DATEPICKER,
      fieldName: 'updatedAt',
      label: t('view-user-form.modified-date-input'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: true,
    },
  ];

  return (
    <>
      <Title level={5} className={styles.userFormRowTitle}>
        <SettingOutlined /> {t('view-user-form.metadata-section-title')}
      </Title>
      <DarwinRow className={styles.userFormRow} gutter={16}>
        <DarwinCol span={12}>
          {firstColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        <DarwinCol span={12}>
          {secondColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        {isEditForm && (
          <>
            <DarwinCol span={12}>
              <NewUserAssociatedProductsAndProcessesInput
                data={productsNames}
                disabled={true}
                fieldName="associatedProducts"
              />
            </DarwinCol>
            <DarwinCol span={12}>
              <NewUserAssociatedProductsAndProcessesInput
                data={processesNames}
                disabled={true}
                fieldName="associatedProcesses"
              />
            </DarwinCol>
          </>
        )}
      </DarwinRow>
    </>
  );
};
